import React, { useEffect, useState } from 'react';
import { ClassOption, FilterOptions, ReportFilters } from '../types';

interface ReportFilterBarProps {
  filters: ReportFilters;
  options?: FilterOptions;
  classes?: ClassOption[];
  onChange: (patch: Partial<ReportFilters>) => void;
  onClear: () => void;
  showClass?: boolean;
}

const INPUT_CLASS =
  'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MIN_YEAR = 2000;

const isCompleteDate = (v: string) => DATE_RE.test(v) && Number(v.slice(0, 4)) >= MIN_YEAR;

// Ô ngày giữ bản nháp riêng: khi gõ năm bằng bàn phím, Chrome phát giá trị trung gian (0002-…, 0202-…)
// và '' khi ô dở dang. Nếu ô điều khiển thẳng bởi bộ lọc, giá trị bị bỏ qua sẽ bị React đặt lại làm
// Chrome phát '' ở phím sau và xoá mất ngày. Chỉ ngày đầy đủ hợp lệ mới đẩy lên bộ lọc (một lần gọi API).
const DateInput: React.FC<{
  id: string;
  value?: string;
  onCommit: (value: string | undefined) => void;
}> = ({ id, value, onCommit }) => {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);

  const handleChange = (next: string) => {
    setDraft(next);
    if (isCompleteDate(next)) onCommit(next);
  };

  // Rời ô/Enter: ô trống nghĩa là xoá ngày, giá trị dở dang thì trả về ngày đang lọc
  const settle = () => {
    if (draft === '') onCommit(undefined);
    if (draft === '' || !isCompleteDate(draft)) setDraft(value ?? '');
  };

  return (
    <input
      id={id}
      type="date"
      value={draft}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={settle}
      onKeyDown={(e) => e.key === 'Enter' && settle()}
      className={INPUT_CLASS}
    />
  );
};

const Field: React.FC<{ id: string; label: string; children: React.ReactNode }> = ({ id, label, children }) => (
  <div className="flex-1 min-w-40">
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    {children}
  </div>
);

const ReportFilterBar: React.FC<ReportFilterBarProps> = ({
  filters,
  options,
  classes = [],
  onChange,
  onClear,
  showClass = false,
}) => {
  const targetValue = filters.book_id
    ? `book:${filters.book_id}`
    : filters.collection_id
      ? `collection:${filters.collection_id}`
      : '';

  // Sách và bộ sách chung một ô nhưng API nhận hai tham số riêng và loại trừ nhau
  const handleTargetChange = (value: string) => {
    const [kind, ...rest] = value.split(':');
    const id = rest.join(':');
    onChange({
      book_id: kind === 'book' && id ? id : undefined,
      collection_id: kind === 'collection' && id ? id : undefined,
    });
  };


  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="flex flex-wrap items-center gap-4">
        <Field id="report-from" label="Từ ngày">
          <DateInput id="report-from" value={filters.from} onCommit={(v) => onChange({ from: v })} />
        </Field>

        <Field id="report-to" label="Đến ngày">
          <DateInput id="report-to" value={filters.to} onCommit={(v) => onChange({ to: v })} />
        </Field>

        <Field id="report-school" label="Trường">
          <select
            id="report-school"
            value={filters.school_id ?? ''}
            onChange={(e) => onChange({ school_id: e.target.value || undefined })}
            className={INPUT_CLASS}
          >
            <option value="">Tất cả trường</option>
            {options?.schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>

        {showClass && (
          <Field id="report-class" label="Lớp">
            <select
              id="report-class"
              value={filters.class_id ?? ''}
              disabled={!filters.school_id}
              onChange={(e) => onChange({ class_id: e.target.value || undefined })}
              className={INPUT_CLASS}
            >
              <option value="">{filters.school_id ? 'Tất cả lớp' : 'Chọn trường trước'}</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field id="report-target" label="Sách / Bộ sách">
          <select
            id="report-target"
            value={targetValue}
            onChange={(e) => handleTargetChange(e.target.value)}
            className={INPUT_CLASS}
          >
            <option value="">Tất cả</option>
            <optgroup label="Sách">
              {options?.books.map((b) => (
                <option key={b.id} value={`book:${b.id}`}>
                  {b.title}
                </option>
              ))}
            </optgroup>
            <optgroup label="Bộ sách">
              {options?.collections.map((c) => (
                <option key={c.id} value={`collection:${c.id}`}>
                  {c.title}
                </option>
              ))}
            </optgroup>
          </select>
        </Field>

        <Field id="report-batch" label="Lô">
          <select
            id="report-batch"
            value={filters.batch_id ?? ''}
            onChange={(e) => onChange({ batch_id: e.target.value || undefined })}
            className={INPUT_CLASS}
          >
            <option value="">Tất cả lô</option>
            {options?.batches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>

        <div className="flex items-end">
          <button
            type="button"
            onClick={onClear}
            className="mt-6 px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-gray-100 border border-gray-300 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Xoá lọc
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportFilterBar;
